import type { DocumentSnapshot } from '@/features/types';
import { createContext, useContext, useSyncExternalStore } from 'react';

/**
 * THE EDITOR'S HANDLE ON THE DOCUMENT — ADR-0006, ADR-0003.
 *
 * `CodeMirrorHost` has to subscribe to the store directly rather than receive a snapshot as a
 * prop: a React re-render per keystroke of a 2 MiB document is the cost this architecture
 * exists to avoid, and `ViewDescriptor` hands a panel `{ view, api }` — a projection and a
 * command channel, neither of which can carry a per-keystroke write channel.
 *
 * So the store arrives through context, and the context is declared HERE rather than in
 * `CodeMirrorHost.tsx` for a load-bearing reason: the provider is mounted by `src/app/`, which
 * is eager, while the panel is `import()`ed lazily by `ViewDescriptor.load()`. If the provider
 * lived in the panel's module, mounting it would pull CodeMirror into the initial chunk and the
 * laziness would be decorative.
 *
 * ── WHY A STRUCTURAL INTERFACE AND NOT `DocumentStore` ────────────────────────────────────
 *
 * `DocumentStore` lives in `src/features/document/`, and a feature may not deep-import a
 * neighbouring feature (gate 2, ADR-0003). That constraint turns out to be the better design
 * anyway: the editor needs exactly three methods, `DocumentStore` satisfies them structurally
 * with no `implements` clause and no coupling in either direction, and a test can drive the
 * host with twenty lines of fake instead of the real store plus a real analyzer.
 */
export interface EditorDocumentSource {
  subscribe(listener: () => void): () => void;
  getSnapshot(): DocumentSnapshot;
  /**
   * The editor's own edits going up. MUST be a no-op when the text is unchanged — that is
   * where the echo of a programmatic replacement is broken (ADR-0006, rule 1).
   */
  setFromEditor(text: string): void;
}

export const EditorDocumentContext = createContext<EditorDocumentSource | null>(null);

/** Throws by name rather than returning `null`: a missing provider is a wiring bug. */
export function useEditorDocumentSource(): EditorDocumentSource {
  const source = useContext(EditorDocumentContext);
  if (source === null) {
    throw new Error(
      'useEditorDocumentSource: no <EditorDocumentContext value={store}> above this component. ' +
        'src/app/ mounts it around the view host.',
    );
  }
  return source;
}

/**
 * Read the snapshot as React state.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`: it is the API that exists
 * precisely so an external mutable store cannot tear across a concurrent render, and the store
 * already has the `subscribe` / `getSnapshot` pair it wants. `getSnapshot` MUST return a stable
 * reference between changes or React loops — `DocumentStore` returns the same frozen object
 * until something actually changes, which is the contract this depends on.
 *
 * `CodeMirrorHost` does NOT use this hook. It subscribes imperatively, because it must not
 * re-render on every keystroke; everything else in the panel does.
 */
export function useDocumentSnapshot(source: EditorDocumentSource): DocumentSnapshot {
  return useSyncExternalStore(
    (listener) => source.subscribe(listener),
    () => source.getSnapshot(),
    () => source.getSnapshot(),
  );
}
